import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TopNavbar from '@/components/layout/TopNavbar';
import Footer from '@/components/home/Footer';
import PrePaymentSignup from '@/components/auth/PrePaymentSignup';
import { StripePricingTable } from '@/components/auth/StripePricingTable';
import { useAuth } from '@/hooks/useAuth';

const SignupFlow: React.FC = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  const [showPricingTable, setShowPricingTable] = useState(false);
  const { user } = useAuth();

  // If user is already authenticated, show pricing table immediately
  React.useEffect(() => {
    if (user) {
      setShowPricingTable(true);
    }
  }, [user]);

  const handleSignupComplete = () => {
    setShowPricingTable(true);
  };

  const handleBackToSignup = () => {
    setShowPricingTable(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Sign Up - Hivetivity | Get Started with Your Free Trial</title>
        <meta 
          name="description" 
          content="Create your Hivetivity account and start your 14-day free trial. No credit card required to get started." 
        />
        <meta 
          name="keywords" 
          content="hivetivity signup, youth program software trial, dance studio software signup, activity center registration" 
        />
        <link rel="canonical" href="https://hivetivity.com/signup" />
      </Helmet>
      
      {/* Top Navigation */}
      <TopNavbar />
      
      {/* Main content with padding for navbar */}
      <div className="pt-16 pb-16">
        {/* Progress Indicator */}
        <section className="py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-4 mb-8">
              {/* Step 1: Account Creation */}
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  showPricingTable ? 'bg-green-500 text-white' : 'bg-[#FA8C00] text-white'
                }`}>
                  {showPricingTable ? <CheckCircle className="h-5 w-5" /> : '1'}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  showPricingTable ? 'text-green-600' : 'text-[#FA8C00]'
                }`}>
                  Create Account
                </span>
              </div>
              
              {/* Connector */}
              <div className={`w-16 h-1 rounded ${
                showPricingTable ? 'bg-green-500' : 'bg-gray-200'
              }`} />
              
              {/* Step 2: Choose Plan */}
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  showPricingTable ? 'bg-[#FA8C00] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  2
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  showPricingTable ? 'text-[#FA8C00]' : 'text-gray-500'
                }`}>
                  Choose Plan
                </span>
              </div>
            </div>
          </div>
        </section>

        {!showPricingTable ? (
          /* Step 1: Account Creation */
          <section className="py-8 px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8"
              >
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Get Started with Your Free Trial
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Create your account to start your 14-day free trial. No credit card required to get started.
                  {selectedPlan && ` You've selected the ${selectedPlan} plan.`}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <PrePaymentSignup 
                  onSignupComplete={handleSignupComplete}
                  selectedPlan={selectedPlan || undefined}
                />
              </motion.div>
            </div>
          </section>
        ) : (
          /* Step 2: Plan Selection */
          <section className="py-8 px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8"
              >
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Choose Your Plan
                </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
                  Welcome! Your account is ready. Now choose the perfect plan for your business.
                </p>
                
                {/* Back Button */}
                {!user && (
                  <Button
                    variant="outline"
                    onClick={handleBackToSignup}
                    className="mb-6"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Account Setup
                  </Button>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-full"
              >
                <StripePricingTable
                  pricingTableId="prctbl_1RW8D1L45zl3J0lpnBvHMSEu"
                  publishableKey="pk_live_51PH8I8L45zl3J0lp1OnJ74LmkQPXtm1YLk1sNFXc601fAZJDZuNav6mgel5iJlu8uFHkaKKCryTR79Cpbkn01LtL00pi3hy7dr"
                  customerEmail={user?.email}
                />
              </motion.div>
            </div>
          </section>
        )}
        
        {/* Benefits Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Why Choose Hivetivity?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#FA8C00]/10 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-[#FA8C00]" />
                </div>
                <h3 className="font-semibold mb-2">14-Day Free Trial</h3>
                <p className="text-gray-600 text-sm">Full access to all features, no restrictions</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#4ba3c7]/10 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-[#4ba3c7]" />
                </div>
                <h3 className="font-semibold mb-2">No Setup Fees</h3>
                <p className="text-gray-600 text-sm">Get started immediately with no hidden costs</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#7ad4a0]/10 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-[#7ad4a0]" />
                </div>
                <h3 className="font-semibold mb-2">Cancel Anytime</h3>
                <p className="text-gray-600 text-sm">No long-term commitments or cancellation fees</p>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignupFlow;